import { icon, node } from "~/util/dom";
import ReportFormField from "./index";
import ReportFormFileloader from "./fileloader";
import ReportFormImage from "./image";
import ReportFormFieldTextarea from "./textarea";
import ReportFormFieldInput from "./input";
import Opening from "~/content/opening";
import { getDetailItems } from "~/report/util";
import { ICommonOpts } from "~/report/interfaces";
import { _ } from "~/i18n";
import { validateEmail } from "../options";
import { ERROR } from "../const";
import { getPhotoMediaId } from "../provider";

interface IOpts extends ICommonOpts {
	container: HTMLElement;
	label: string;
	values?: any;
	opening?: any;
	onChange: (value: any, data?: object) => void;
	onUploadStart?: () => void;
	onUploadError?: () => void;
}

export default class ReportFormFieldDetail extends ReportFormField {
	#opts: IOpts;

	#fieldNode: HTMLElement;

	#state: any = {};

	#opening: ReportFormFieldTextarea;

	#openingNode: HTMLElement;

	#openingPhotoNode: HTMLElement;

	#openingPhoto: ReportFormImage;

	#openingFileloader: ReportFormFileloader;

	#isLoading: boolean;

	#openingPhotoRemoved: boolean;

	constructor(opts: IOpts) {
		super(opts);
		this.#opts = {
			onUploadStart: () => { /**/ },
			onUploadError: () => { /**/ },
			parentName: "",
			values: {},
			opening: null,
			...opts,
		};
		this.#openingPhotoRemoved = false;
		this.#build();
	}

	get hasNoOpening() {
		// eslint-disable-next-line prefer-spread
		return !Array.apply(null, { length: 7 }).some((value, index) => this.#state[`opening-${index + 1}`])
			&& !this.#state.openingMediaId
			&& !this.#isLoading;
	}

	handleEvent(event) {
		const { target } = event;

		if (event.type === "click") {
			this.#buildDetail();
			this.#opts.container.dispatchEvent(new Event("resize-content"));
		}
		if (event.type === "focus") {
			if (target === this.#openingFileloader.inputNode
				|| (this.#openingPhoto && target === this.#openingPhoto.deleteButton)
			) {
				this.#openingNode.classList.add("focused");
			}
		}
		if (event.type === "blur") {
			if (this.#openingNode.classList.contains("focused")
				&& (target === this.#openingFileloader.inputNode
				|| (this.#openingPhoto && target === this.#openingPhoto.deleteButton))
			) {
				this.#openingNode.classList.remove("focused");
			}
		}
	}

	async #addOpeningPhoto(file: File) {
		this.#isLoading = true;
		this.#openingFileloader.hide();
		this.#state.openingMediaId = 0;
		this.#openingPhotoRemoved = false;

		this.#opts.onUploadStart();

		this.#openingPhoto = new ReportFormImage({
			file,
			parent: this.#openingPhotoNode,
			onDomChange: () => {},
			onRemove: () => {
				this.#removeOpeningPhoto();
			},
			upload: async classThis => {
				try {
					classThis.uploadProgress(0);

					const photoData = await getPhotoMediaId({
						file,
						opening: true,
						onUpdate: percent => {
							classThis.uploadProgress(percent);
						},
					});

					// spravne id
					if (photoData.mediaId > 0) {
						if (this.#openingPhotoRemoved) {
							this.#removeOpeningPhoto();
						} else {
							/* eslint-disable-next-line */
							this.#state.openingMediaId = photoData.mediaId;
						}

						return "OK";
					}
				} catch (exc) {
					/* eslint-disable no-console */
					console.log(exc);
				}

				this.#opts.onUploadError();

				return ERROR.UPLOAD;
			},
			minWidth: 0,
			minHeight: 0,
		});

		await this.#openingPhoto.settingFile;

		if (this.#state.openingMediaId > 0) {
			this.#isLoading = false;
			this.#opts.onChange(this.#state);
			this.#opening.setHeight(false);
			this.#openingPhoto.deleteButton.addEventListener("blur", this);
			this.#openingPhoto.deleteButton.addEventListener("focus", this);
		} else {
			this.#isLoading = false;
			this.#opts.onUploadError();
			this.#openingPhoto.remove();
		}
	}

	#removeOpeningPhoto() {
		this.#openingPhotoRemoved = true;
		this.#state.openingMediaId = 0;
		this.#opts.onChange(this.#state);
		this.#openingPhoto = null;
		this.#openingFileloader.show();
		this.#opening.setHeight(false);
		this.#openingNode.classList.remove("focused");
	}

	#buildDetail() {
		const { values, opening } = this.#opts;

		this.#fieldNode.replaceChildren();
		this.#fieldNode.classList.add("open");

		if (opening) {
			const openingLabelNode = node("div", { className: "form-field simple label" });
			const labelNode = node("span", { innerText: _("report.openingTime") });
			const iconNode = icon("time");

			iconNode.classList.add("type-sign", "no-shrink");
			openingLabelNode.append(iconNode, labelNode);
			const openingInst = new Opening(opening, { log: () => { /**/ } });
			const openingNode = openingInst.getContainer();

			openingNode.querySelector("p.title")?.remove();
			openingNode.classList.add("expanded");
			this.#fieldNode.append(openingLabelNode, openingNode);
		}

		this.#opening = new ReportFormFieldTextarea({
			container: super.container,
			parent: this.#fieldNode,
			parentName: this.#opts.parentName,
			typeSign: icon("time"),
			changeSign: null,
			name: "opening",
			placeholder: opening ? _("detail.content.closed.suggestEdit") : _("report.openingTime"),
			minHeight: 32,
			onChange: value => {
				this.#state.opening = value;
				this.#opts.onChange(this.#state);
			},
		});

		this.#openingNode = this.#opening.container;
		this.#openingPhotoNode = node("div", { className: "photo-holder no-shrink" });
		this.#openingNode.append(this.#openingPhotoNode);
		this.#openingFileloader = new ReportFormFileloader({
			parent: this.#openingPhotoNode,
			icon: icon("image-round"),
			textShort: _("add"),
			textMinimal: _("gallery.uploadPhoto"),
			isMinimized: true,
			onChange: files => {
				this.#addOpeningPhoto(files[0]);
			},
			setMultiple: true,
			accept: "image/png, image/jpeg",
		});
		this.#openingFileloader.inputNode.addEventListener("focus", this);
		this.#openingFileloader.inputNode.addEventListener("blur", this);

		for (const item of getDetailItems()) {
			// eslint-disable-next-line no-new
			new ReportFormFieldInput({
				container: super.container,
				parent: this.#fieldNode,
				parentName: this.#opts.parentName,
				typeSign: icon(item.iconName),
				changeSign: values ? icon("report-edit-location") : null,
				name: item.name,
				placeholder: item.placeholder,
				value: values ? values[item.name] : undefined,
				type: item.type,
				addFormFieldInputClass: true,
				onChange: value => {
					this.#state[item.name] = value;
					this.#opts.onChange(this.#state);
				},
				customClass: item.type === "email"
					? "input-type-email"
					: "",
				validate: item.type === "email"
					? value => value.length === 0
						? {
							message: "",
							status: "valid",
						}
						: validateEmail(value)
					: undefined,
			});
		}

		const textarea = new ReportFormFieldTextarea({
			container: super.container,
			parent: this.#fieldNode,
			parentName: this.#opts.parentName,
			changeSign: null,
			placeholder: _("report.notePlaceholder"),
			label: _("report.note"),
			name: "note",
			onChange: value => {
				this.#state.note = value;
				this.#opts.onChange(this.#state);
			},
		});

		textarea.container.style.marginTop = "24px";
		textarea.container.style.marginBottom = "0";
	}

	#build() {
		this.#fieldNode = node("div", { className: "detail" });
		this.#fieldNode.id = "report-detail";

		this.#buildDetail();
		super.container.append(this.#fieldNode);
		super.container.classList.add("report-detail");
	}
}
